import { z } from "zod";
import { isoDateTime, paginationQuerySchema } from "./common";

export const integrationStatusSchema = z.object({
  environment: z.string(),
  email: z.object({ provider: z.string(), configured: z.boolean() }),
  turnstile: z.object({ configured: z.boolean() }),
  catalogDeployHook: z.object({ configured: z.boolean() }),
  catalogBuildToken: z.object({ configured: z.boolean() }),
  gamepush: z.object({ configured: z.boolean() }),
  roiads: z.object({ enabled: z.boolean() }),
  mediaStorage: z.object({ configured: z.boolean(), publicBaseUrl: z.string().nullable() }),
});
export type IntegrationStatus = z.infer<typeof integrationStatusSchema>;

/** Known non-secret settings; unknown keys are rejected. */
export const appSettingsSchema = z.object({
  "feedback.notifyEmail": z.email().nullable(),
  "analytics.retentionDays": z.number().int().min(7).max(730),
});
export type AppSettings = z.infer<typeof appSettingsSchema>;
export const appSettingsPatchSchema = appSettingsSchema.partial();

export const APP_SETTINGS_DEFAULTS: AppSettings = {
  "feedback.notifyEmail": null,
  "analytics.retentionDays": 90,
};

export const auditEntrySchema = z.object({
  id: z.number().int(),
  actorType: z.string(),
  actorUserId: z.string().nullable(),
  actorName: z.string().nullable(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  data: z.record(z.string(), z.unknown()).nullable(),
  createdAt: isoDateTime,
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const auditListQuerySchema = paginationQuerySchema.extend({
  entityType: z.string().max(60).optional(),
  entityId: z.string().max(120).optional(),
  action: z.string().max(120).optional(),
});

export const dashboardSchema = z.object({
  catalog: z.object({
    publishedGames: z.number().int(),
    draftGames: z.number().int(),
    changedSinceLastRelease: z.number().int(),
    lastReleaseAt: isoDateTime.nullable(),
  }),
  traffic: z.object({
    viewsToday: z.number().int(),
    launchesToday: z.number().int(),
    onlineNow: z.number().int(),
    launches7d: z.array(z.object({ day: z.string(), launches: z.number().int() })),
  }),
  feedback: z.object({ newReports: z.number().int(), newContacts: z.number().int() }),
  recentReports: z.array(
    z.object({
      id: z.number().int(),
      gameName: z.string().nullable(),
      description: z.string(),
      createdAt: isoDateTime,
    }),
  ),
});
export type Dashboard = z.infer<typeof dashboardSchema>;
